import { useEffect, useMemo, useRef, useState } from "react";
import shp from "shpjs";
import * as toGeoJSON from "@tmcw/togeojson";

import { fetchDataSourcePreview } from "../../api/dataSources";
import { type FilestoreFile, parseFilestoreFile } from "../../api/filestore";
import { BASE_TILE_METERS, PROJECT_SIZE_GRID_DIM } from "../../constants/project";
import { useAuth } from "../../contexts/AuthContext";
import type { Layer } from "../../types/layer";
import type { ProjectBoundary } from "../../types/project";
import PinLayer from "../layer_components/PinLayer";
import ProjectBoundingBoxLayer from "../layer_components/ProjectBoundingBoxLayer";
import type { CreateProjectData } from "./types";

export type Tile = { col: number; row: number };

/** The base square is tile (0,0): its SW corner is the origin, its span the tile size. */
export interface TileGrid {
  originLng: number;
  originLat: number;
  tileWidthDeg: number;
  tileHeightDeg: number;
}

export interface SourcePreview {
  loading: boolean;
  count: number;
  truncated: boolean;
  errors: string[];
}

export interface ProjectAreaPreview {
  /** Pin, one outline per tile, and the property boundaries, for the map. */
  layers: Layer[];
  /** Tile expansion is gated behind the 'infinite_project' capability. */
  canExpand: boolean;
  tileGrid: TileGrid | null;
  /** The size grid plus any manually-added tiles beyond it. */
  selectedTiles: Tile[];
  expansionTiles: Tile[];
  addTile: (col: number, row: number) => void;
  removeTile: (col: number, row: number) => void;
  /** The property file parsed and is drawn on the map. */
  propertyFileReady: boolean;
  /** Parcels from the selected property boundary sources over the project area. */
  sourcePreview: SourcePreview;
}

const bboxToPolygon = (top: number, left: number, bottom: number, right: number): ProjectBoundary => ({
  type: "Polygon",
  coordinates: [[[left, bottom], [right, bottom], [right, top], [left, top], [left, bottom]]],
});

const bufferLatLng = (lat: number, lng: number, bufferMetersY: number, bufferMetersX: number) => {
  const earthRadius = 6371000;
  const latBuffer = (bufferMetersY / earthRadius) * (180 / Math.PI);
  const lngBuffer = ((bufferMetersX / earthRadius) * (180 / Math.PI)) / Math.cos((lat * Math.PI) / 180);
  return {
    top: lat + latBuffer,
    bottom: lat - latBuffer,
    left: lng - lngBuffer,
    right: lng + lngBuffer,
  };
};

// "transient-" ids: Map removes these from the map once they leave the layer
// list (a cleared property file, a closed new-project flow).
const PIN_ID = "transient-new-project-pin";
const PROPERTY_FILE_ID = "transient-new-project-property-file";
const PROPERTY_SOURCES_ID = "transient-new-project-property-sources";

const EMPTY_TILES: Tile[] = [];

/**
 * Everything the create-project wizard draws for the project's area, shared by
 * the full-page wizard's Location step and the listing page's new-project
 * flow: the pin, the tile grid (size plus manual expansion), the uploaded
 * property file, and the parcels the selected property sources hold over the
 * area. The location comes from `data`; whoever owns the map feeds clicks in.
 */
export function useProjectAreaPreview(
  data: CreateProjectData,
  onUpdateData: (updates: Partial<CreateProjectData>) => void
): ProjectAreaPreview {
  const { user } = useAuth();
  const selectedLatLng = data.selectedLatLng;

  // Tile expansion uses the same backend endpoint as the live project-expand
  // feature, which is gated behind the 'infinite_project' capability. Users
  // without it just get the single square.
  const canExpand =
    !!user?.is_staff || !!user?.capabilities?.some((cap) => cap.codename === "infinite_project");

  const expansionTiles = data.expansionTiles ?? EMPTY_TILES;
  // The selected size is an NxN grid of base tiles (cols/rows 0..N-1), all
  // created during init. Manual expansion adds tiles beyond that grid.
  const gridDim = PROJECT_SIZE_GRID_DIM[data.projectSize] ?? 1;
  const gridTiles = useMemo(() => {
    const tiles: Tile[] = [];
    for (let col = 0; col < gridDim; col++) {
      for (let row = 0; row < gridDim; row++) tiles.push({ col, row });
    }
    return tiles;
  }, [gridDim]);
  const selectedTiles = useMemo(() => [...gridTiles, ...expansionTiles], [gridTiles, expansionTiles]);

  const pinLayer = PinLayer(PIN_ID, "Pin", selectedLatLng);

  // Property boundary preview states
  const [propertyGeojson, setPropertyGeojson] = useState<object | null>(null);
  const [propertyLayerStale, setPropertyLayerStale] = useState(false);
  // Parcels from the selected property boundary data sources, fetched from
  // the backend for the project area (the union of the selected tiles) so
  // the map shows what the project will load. Re-fetched, debounced, when
  // the location, size, tiles or sources change.
  const [sourcePropertyGeojson, setSourcePropertyGeojson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [sourcePropertyLayerStale, setSourcePropertyLayerStale] = useState(false);
  const [sourcePreview, setSourcePreview] = useState<SourcePreview>({
    loading: false,
    count: 0,
    truncated: false,
    errors: [],
  });
  // Bumped per fetch so a late response from a previous area is ignored.
  const previewRequestRef = useRef(0);

  // Parse property files for map preview
  useEffect(() => {
    const parseFiles = async () => {
      if (!data.propertyFiles) {
        setPropertyGeojson(null);
        return;
      }
      try {
        let parsed: unknown = null;

        if (data.propertyFiles instanceof File) {
          const file = data.propertyFiles;
          const ext = file.name.split(".").pop()?.toLowerCase();

          if (ext === "zip") {
            const arrayBuffer = await file.arrayBuffer();
            parsed = await shp(arrayBuffer);
          } else if (ext === "kml") {
            const text = await file.text();
            const dom = new DOMParser().parseFromString(text, "application/xml");
            parsed = toGeoJSON.kml(dom);
          } else if (ext === "geojson" || ext === "json") {
            const text = await file.text();
            parsed = JSON.parse(text);
          }
        } else {
          parsed = await parseFilestoreFile((data.propertyFiles as FilestoreFile).id);
        }

        if (parsed) {
          setPropertyGeojson(parsed as object);
          setPropertyLayerStale(true);
        }
      } catch (error) {
        console.error("Error parsing property file:", error);
      }
    };

    parseFiles();
  }, [data.propertyFiles]);

  // The base (small) tile bounding box from the selected location. Every size
  // is a tiling of this base tile; the grid (origin + tile size) drives the rest.
  const baseBoundingBox = selectedLatLng
    ? bufferLatLng(selectedLatLng.lat, selectedLatLng.lng, BASE_TILE_METERS.y, BASE_TILE_METERS.x)
    : null;

  // North is +row, east is +col, matching the expansion grid.
  const tileGrid: TileGrid | null = baseBoundingBox
    ? {
        originLng: baseBoundingBox.left,
        originLat: baseBoundingBox.bottom,
        tileWidthDeg: baseBoundingBox.right - baseBoundingBox.left,
        tileHeightDeg: baseBoundingBox.top - baseBoundingBox.bottom,
      }
    : null;

  // One outlined rectangle per selected tile (base + expansions).
  const boundingBoxLayers = tileGrid
    ? selectedTiles.map(({ col, row }) => {
        const tileLeft = tileGrid.originLng + col * tileGrid.tileWidthDeg;
        const tileBottom = tileGrid.originLat + row * tileGrid.tileHeightDeg;
        const tileRight = tileLeft + tileGrid.tileWidthDeg;
        const tileTop = tileBottom + tileGrid.tileHeightDeg;
        const isBase = col === 0 && row === 0;
        return ProjectBoundingBoxLayer(
          `bbox-tile-${col}-${row}`,
          isBase ? "Project Area" : `Tile ${col},${row}`,
          bboxToPolygon(tileTop, tileLeft, tileBottom, tileRight),
          false, // showName
          false // fill
        );
      })
    : [];

  // The area the project will load: the union of every selected tile.
  const projectBbox = useMemo(() => {
    if (!tileGrid || selectedTiles.length === 0) return null;
    let minCol = Infinity;
    let maxCol = -Infinity;
    let minRow = Infinity;
    let maxRow = -Infinity;
    for (const t of selectedTiles) {
      minCol = Math.min(minCol, t.col);
      maxCol = Math.max(maxCol, t.col);
      minRow = Math.min(minRow, t.row);
      maxRow = Math.max(maxRow, t.row);
    }
    return {
      left: tileGrid.originLng + minCol * tileGrid.tileWidthDeg,
      right: tileGrid.originLng + (maxCol + 1) * tileGrid.tileWidthDeg,
      bottom: tileGrid.originLat + minRow * tileGrid.tileHeightDeg,
      top: tileGrid.originLat + (maxRow + 1) * tileGrid.tileHeightDeg,
    };
    // tileGrid is rebuilt every render; its numbers are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileGrid?.originLng, tileGrid?.originLat, tileGrid?.tileWidthDeg, tileGrid?.tileHeightDeg, selectedTiles]);
  const projectBboxKey = projectBbox
    ? [projectBbox.left, projectBbox.bottom, projectBbox.right, projectBbox.top].map((v) => v.toFixed(6)).join(",")
    : "";
  const propertySourceKey = data.selectedPropertySources.map((s) => s.id).join(",");

  useEffect(() => {
    const sources = data.selectedPropertySources;
    if (!projectBbox || sources.length === 0) {
      previewRequestRef.current += 1;
      setSourcePropertyGeojson(null);
      setSourcePreview({ loading: false, count: 0, truncated: false, errors: [] });
      return;
    }
    const requestId = ++previewRequestRef.current;
    const bbox = projectBbox;
    setSourcePreview((prev) => ({ ...prev, loading: true }));
    // Debounced: dragging the pin or stepping through sizes fires many changes.
    const timer = setTimeout(async () => {
      const results = await Promise.allSettled(sources.map((s) => fetchDataSourcePreview(s.id, bbox)));
      if (requestId !== previewRequestRef.current) return;
      const features: GeoJSON.Feature[] = [];
      const errors: string[] = [];
      let truncated = false;
      results.forEach((result, i) => {
        if (result.status === "fulfilled") {
          features.push(...result.value.features);
          truncated = truncated || result.value.truncated;
        } else {
          const message = (result.reason as { message?: string } | null)?.message;
          errors.push(`${sources[i].name}: ${message || "preview unavailable"}`);
        }
      });
      setSourcePropertyGeojson({ type: "FeatureCollection", features });
      setSourcePropertyLayerStale(true);
      setSourcePreview({ loading: false, count: features.length, truncated, errors });
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectBboxKey, propertySourceKey]);

  // Property boundary layer - render if property files were uploaded
  const propertyBoundaryLayer = propertyGeojson
    ? {
        id: PROPERTY_FILE_ID,
        name: "Property Boundaries",
        source: propertyGeojson,
        layerStyle: {
          id: PROPERTY_FILE_ID,
          type: "line" as const,
          paint: {
            "line-color": "#00d4ad",
            "line-width": 2,
            "line-opacity": 0.8,
          },
        },
        toolbarButton: null,
        active: true,
        stale: propertyLayerStale,
        setStale: setPropertyLayerStale,
      }
    : null;

  // Parcels from the selected property boundary sources, drawn like the
  // file's: on the map they are all just the project's property boundaries.
  const sourcePropertyLayer = sourcePropertyGeojson
    ? {
        id: PROPERTY_SOURCES_ID,
        name: "Property Boundaries (data sources)",
        source: sourcePropertyGeojson,
        layerStyle: {
          id: PROPERTY_SOURCES_ID,
          type: "line" as const,
          paint: {
            "line-color": "#00d4ad",
            "line-width": 2,
            "line-opacity": 0.8,
          },
        },
        toolbarButton: null,
        active: true,
        stale: sourcePropertyLayerStale,
        setStale: setSourcePropertyLayerStale,
      }
    : null;

  const addTile = (col: number, row: number) => {
    // Already part of the auto-created size grid, or already added.
    if (col >= 0 && col < gridDim && row >= 0 && row < gridDim) return;
    if (expansionTiles.some((t) => t.col === col && t.row === row)) return;
    onUpdateData({ expansionTiles: [...expansionTiles, { col, row }] });
  };

  const removeTile = (col: number, row: number) => {
    onUpdateData({ expansionTiles: expansionTiles.filter((t) => !(t.col === col && t.row === row)) });
  };

  const layers = [
    ...(selectedLatLng ? [pinLayer] : []),
    ...boundingBoxLayers,
    ...(propertyBoundaryLayer ? [propertyBoundaryLayer] : []),
    ...(sourcePropertyLayer ? [sourcePropertyLayer] : []),
  ] as Layer[];

  return {
    layers,
    canExpand,
    tileGrid,
    selectedTiles,
    expansionTiles,
    addTile,
    removeTile,
    propertyFileReady: !!propertyGeojson,
    sourcePreview,
  };
}
